import { baixarArquivo } from './http';

/**
 * "Extrair xlsx" — a lista da tela em planilha (`POST /api/planilhas/xlsx`).
 *
 * A tela diz quais colunas e manda as linhas que já tem; o servidor só formata
 * (cabeçalho, filtro, moeda como número). Assim a planilha tem as colunas que
 * o operador vê, com os mesmos rótulos.
 */

export type FormatoDaColuna = 'texto' | 'numero' | 'moeda' | 'percentual' | 'data';

export interface ColunaDaPlanilha<T> {
  readonly titulo: string;
  /** `percentual` em pontos percentuais (15 é 15%); `data` em `AAAA-MM-DD`. */
  readonly formato?: FormatoDaColuna;
  /** Em caracteres, como o Excel mede. */
  readonly largura?: number;
  readonly valor: (linha: T) => string | number | null | undefined;
}

/** Uma aba já em valores de planilha — o que vai no corpo do pedido. */
export interface AbaPronta {
  readonly nome: string;
  readonly colunas: readonly {
    readonly titulo: string;
    readonly formato: FormatoDaColuna;
    readonly largura?: number;
  }[];
  readonly linhas: readonly (readonly (string | number | null)[])[];
}

/** As linhas da tela, pelas colunas da planilha, em valores que o servidor aceita. */
export function aba<T>(
  nome: string,
  colunas: readonly ColunaDaPlanilha<T>[],
  linhas: readonly T[],
): AbaPronta {
  return {
    nome,
    colunas: colunas.map((c) => ({
      titulo: c.titulo,
      formato: c.formato ?? 'texto',
      ...(c.largura === undefined ? {} : { largura: c.largura }),
    })),
    linhas: linhas.map((l) =>
      colunas.map((c) => {
        const v = c.valor(l);
        return v === undefined || (typeof v === 'number' && !Number.isFinite(v)) ? null : v;
      }),
    ),
  };
}

/** Baixa `<titulo> <dia>.xlsx` com as abas dadas — um relatório pode ter mais de uma tabela. */
export function baixarXlsx(titulo: string, abas: readonly AbaPronta[]): Promise<void> {
  return baixarArquivo('/planilhas/xlsx', `${titulo}.xlsx`, {
    metodo: 'POST',
    corpo: { titulo, abas },
  });
}

/** Baixa a lista numa planilha de uma aba só. */
export function extrairXlsx<T>(
  titulo: string,
  colunas: readonly ColunaDaPlanilha<T>[],
  linhas: readonly T[],
): Promise<void> {
  return baixarXlsx(titulo, [aba(titulo, colunas, linhas)]);
}

/**
 * Todas as páginas de uma lista paginada no servidor, para a planilha levar a
 * lista inteira e não só a página da tela.
 */
export async function todasAsPaginas<T>(
  buscar: (pagina: number) => Promise<{ readonly itens: readonly T[]; readonly total: number }>,
): Promise<T[]> {
  const itens: T[] = [];
  for (let pagina = 1; ; pagina += 1) {
    const resposta = await buscar(pagina);
    itens.push(...resposta.itens);
    if (resposta.itens.length === 0 || itens.length >= resposta.total) return itens;
  }
}

/**
 * O dia de uma data da API, para a coluna `data` da planilha (que lê só o dia).
 *
 * - Data sem hora (`@db.Date`) chega como `2026-09-30` ou como meia-noite UTC
 *   (`2026-09-30T00:00:00.000Z`): o dia é esse, e convertê-la para Brasília a
 *   jogaria para o dia 29.
 * - Instante de verdade (`2026-10-01T01:00:00Z`) vira o dia em que caiu aqui —
 *   22h do dia 30 em Brasília, não o dia 1º do UTC.
 */
export function diaLocal(instante: string | null | undefined): string | null {
  if (instante === null || instante === undefined) return null;
  const soData = /^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.0+)?Z)?$/.exec(instante);
  if (soData !== null) return soData[1] ?? null;
  const d = new Date(instante);
  if (Number.isNaN(d.getTime())) return null;
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}`;
}

/** Sim/Não, como a planilha mostra um booleano. */
export const simOuNao = (v: boolean): string => (v ? 'Sim' : 'Não');
